import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { Row, Col, Card, CardBody, Label } from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "formik"

//redux
import { useSelector, useDispatch } from "react-redux"
import { Link } from "react-router-dom"
import withRouter from "components/Common/withRouter"

// Formik Validation
import * as Yup from "yup"
import { useFormik } from "formik"

import logo from "../../assets/images/logo.svg"
import {
  resetPasswordSchema,
  verifyOTPSchema,
  verifyPasswordSchema,
} from "components/Common/validation"
import {
  changePassword,
  forgotPassword,
  verifyOtp,
} from "store/actions/authAction"
import { showAlert } from "store/reducers/alertReducer"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const OTP_RESEND_COOLDOWN_SECONDS = 60

const getRemainingCooldown = sentAt => {
  if (!sentAt) return 0
  const elapsedSeconds = Math.floor((Date.now() - Number(sentAt)) / 1000)
  return Math.max(0, OTP_RESEND_COOLDOWN_SECONDS - elapsedSeconds)
}

const formatRemaining = (seconds, t) => {
  if (!seconds || seconds <= 0) return ""
  if (seconds < 60) {
    return seconds === 1 ? t("second_singular") : t("second_plural", { count: seconds })
  }
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  const minsStr = mins === 1 ? t("minute_singular") : t("minute_plural", { count: mins })
  if (secs === 0) return minsStr
  const secsStr = secs === 1 ? t("second_singular") : t("second_plural", { count: secs })
  return `${minsStr} ${secsStr}`
}

const VerifyPassword = props => {
  const { t } = useTranslation()
  //meta title
  document.title = t("Verify Password | Sign Burger")

  const [tempOtp, TempOtp] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [resending, setResending] = useState(false)
  const [cooldownSeconds, setCooldownSeconds] = useState(0)

  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined
    const interval = setInterval(() => {
      setCooldownSeconds(prev => (prev <= 1 ? 0 : prev - 1))
    }, 1000)
    return () => clearInterval(interval)
  }, [cooldownSeconds])

  useEffect(() => {
    const sentAt = Number(sessionStorage.getItem("forgotOtpSentAt") || 0)
    setCooldownSeconds(getRemainingCooldown(sentAt))
  }, [])

  const dispatch = useDispatch()

  const {
    loading,
    verifyPasswordStep,
    verifyPasswordError,
    changePasswordError,
    varifyPasswordSuccesMsg,
    changePasswordSuccessMsg,
    error,
    message,
    actionType,
  } = useSelector(state => state.Login)
  // console.log(verifyPasswordStep)
  const handleOtpVerify = values => {
    const forgotEmail = sessionStorage.getItem("forgotEmail")
    const payload = {
      email: forgotEmail,
      otp: values.otp,
    }
    TempOtp(values.otp)
    dispatch(verifyOtp({ ...payload, history: props.router.navigate })).then(
      action => {
        const { statusCode, status, msg, errorCode, data } = (action && action.payload) || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          let displayMsg = msg
          if (errorCode === "ACCOUNT_LOCKED" && data && data.remainingSeconds) {
            displayMsg = t("Too many failed login attempts. Your account is temporarily locked. Please try again after {{time}}.", {
              time: formatRemaining(data.remainingSeconds, t),
            })
          } else if (errorCode === "INCORRECT_OTP" && data && data.remainingAttempts !== undefined) {
            displayMsg = t("{{msg}} {{remaining}} attempt(s) remaining.", {
              msg,
              remaining: data.remainingAttempts,
            })
          }
          dispatch(showAlert({ text: displayMsg, type: "error" }))
        }
      }
    )
  }

  const handleResendOtp = () => {
    if (cooldownSeconds > 0) return
    const forgotEmail = sessionStorage.getItem("forgotEmail")
    if (!forgotEmail) {
      props.router.navigate("/login")
      return
    }

    setResending(true)
    dispatch(forgotPassword({ email: forgotEmail, history: props.router.navigate }))
      .then(action => {
        const { statusCode, status, msg, errorCode, data } = (action && action.payload) || {}
        if (statusCode === 200 && status) {
          sessionStorage.setItem("forgotOtpSentAt", Date.now())
          dispatch(showAlert({ text: msg, type: "success" }))
          setCooldownSeconds(OTP_RESEND_COOLDOWN_SECONDS)
        } else {
          let displayMsg = msg
          if (
            (errorCode === "OTP_RESEND_COOLDOWN" ||
              errorCode === "RATE_LIMITED" ||
              errorCode === "OTP_SEND_LIMITED") &&
            data &&
            data.remainingSeconds
          ) {
            displayMsg = t("Too many OTP resend requests. Please try again after {{time}}.", {
              time: formatRemaining(data.remainingSeconds, t),
            })
            setCooldownSeconds(data.remainingSeconds)
          }
          dispatch(showAlert({ text: displayMsg, type: "error" }))
        }
      })
      .finally(() => setResending(false))
  }

  const handlePasswordChange = values => {
    const forgotEmail = sessionStorage.getItem("forgotEmail")
    const verifiedOtp = tempOtp || sessionStorage.getItem("forgotOtp")
    if (!verifiedOtp) {
      dispatch(
        showAlert({
          text: t("Please verify OTP before resetting your password."),
          type: "error",
        })
      )
      return
    }
    const { newPassword, confirmPassword } = values
    const payload = {
      email: forgotEmail,
      otp: verifiedOtp,
      newPassword,
      confirmPassword,
    }
    dispatch(changePassword({ ...payload, history: props.router.navigate }))
      .then(action => {
        const { statusCode, status, msg, errorCode, data } = (action && action.payload) || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          let displayMsg = msg
          if (errorCode === "RATE_LIMITED" && (data && data.remainingSeconds)) {
            // Build the full sentence from the dynamic time only — never
            // prepend the server's static msg in front of it.
            displayMsg = t("Too many password reset attempts. Please try again after {{time}}.", {
              time: formatRemaining(data.remainingSeconds, t),
            })
          }
          dispatch(showAlert({ text: displayMsg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <React.Fragment>
      <div className="account-pages">
        <div className="login_form">
          <Card className="overflow-hidden">
            <div className="bg-primary-subtle">
              <Row>
                <Col xs={12}>
                  <div className="text-primary p-4">
                    <h5 className="text-primary">{t("Welcome Back")} !</h5>
                    <p>{t("Sign in to continue to Sign Burger.")}</p>
                  </div>
                </Col>
              </Row>
            </div>
            <CardBody className="pt-0">
              <div>
                <Link to="/">
                  <div className="avatar-md profile-user-wid mb-4">
                    <span className="avatar-title rounded-circle bg-light">
                      <img
                        src={logo}
                        alt=""
                        className="rounded-circle"
                        height="34"
                      />
                    </span>
                  </div>
                </Link>
              </div>
              <div className="p-2">
                {verifyPasswordStep === "NEW_PASSWORD" ? (
                  <Formik
                    enableReinitialize={true}
                    initialValues={{ newPassword: "", confirmPassword: "" }}
                    validationSchema={verifyPasswordSchema}
                    onSubmit={handlePasswordChange}
                  >
                    {({ values, errors, touched, isSubmitting }) => (
                      <Form className="form-horizontal">
                        <div className="mb-3">
                          <Label className="form-label">
                            {t("New Password")}
                          </Label>
                          <div className="position-relative">
                            <Field
                              name="newPassword"
                              className={`form-control has_common_eye ${
                                touched.newPassword &&
                                errors.newPassword &&
                                "is-invalid"
                              }`}
                              placeholder={t("New Password")}
                              type={showPassword ? "text" : "password"}
                            />
                            <i
                              onClick={() => setShowPassword(!showPassword)}
                              className={`bx bx-${
                                showPassword ? "show-alt" : "hide"
                              } common_eye`}
                            ></i>
                          </div>
                          <TextError name="newPassword" />
                        </div>
                        <div className="mb-3">
                          <Label className="form-label">
                            {t("Confirm Password")}
                          </Label>
                          <div className="position-relative">
                            <Field
                              name="confirmPassword"
                              className={`form-control has_common_eye ${
                                touched.confirmPassword &&
                                errors.confirmPassword &&
                                "is-invalid"
                              }`}
                              placeholder={t("Confirm Password")}
                              type={showConfirmPassword ? "text" : "password"}
                            />
                            <i
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              className={`bx bx-${
                                showConfirmPassword ? "show-alt" : "hide"
                              } common_eye`}
                            ></i>
                          </div>
                          <TextError name="confirmPassword" />
                        </div>
                        <Row className="mb-3">
                          <Col className="text-end">
                            <button
                              className="btn btn-primary w-md "
                              type="submit"
                              disabled={isSubmitting}
                            >
                              {t("Save")}
                            </button>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </Formik>
                ) : (
                  <Formik
                    enableReinitialize={true}
                    initialValues={{ otp: null }}
                    validationSchema={verifyOTPSchema}
                    onSubmit={handleOtpVerify}
                  >
                    {({ values, errors, touched, isSubmitting }) => (
                      <Form className="form-horizontal">
                        <div className="mb-3">
                          <Label className="form-label">
                            {t("Verify OTP")}
                          </Label>
                          <Field
                            name="otp"
                            className={`form-control ${
                              touched.otp && errors.otp && "is-invalid"
                            }`}
                            placeholder={t("Verify OTP")}
                            type="text"
                          />
                          <TextError name="otp" />
                        </div>
                        <Row className="mb-3">
                          <Col className="text-end">
                            <button
                              className="btn btn-primary w-md "
                              type="submit"
                              disabled={loading}
                            >
                              {t("Verify")}
                            </button>
                          </Col>
                        </Row>
                        <div className="text-center mt-2">
                          <button
                            type="button"
                            className="btn btn-link text-primary p-0"
                            onClick={handleResendOtp}
                            disabled={resending || cooldownSeconds > 0}
                          >
                            {cooldownSeconds > 0
                              ? t("Resend OTP in {{time}}", {
                                  time: formatRemaining(cooldownSeconds, t),
                                })
                              : t("Resend OTP")}
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                )}
              </div>
            </CardBody>
          </Card>
          <div className="text-center auth-page-footer">
            <p>
              {t("Go back to")}{" "}
              <Link to="/login" className="font-weight-medium text-black">
                {t("Login")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

VerifyPassword.propTypes = {
  history: PropTypes.object,
}

export default withRouter(VerifyPassword)
